import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useB2BProductExcelParser } from './useB2BProductExcelParser';
import { useBulkCreateB2BProducts } from '@/hooks/use-b2b-products';
import B2BProductUploadTemplate from './B2BProductUploadTemplate';
import B2BProductPreviewTable from './B2BProductPreviewTable';

interface B2BProductUploadProps {
  onSuccess: () => void;
  onCancel: () => void;
}

type UploadStep = 'select' | 'preview' | 'uploading' | 'complete';

const B2BProductUpload: React.FC<B2BProductUploadProps> = ({ onSuccess, onCancel }) => {
  const [uploadStep, setUploadStep] = useState<UploadStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const {
    processFile,
    clearData,
    isProcessing,
    previewData,
    errors,
    hasData,
    hasErrors,
  } = useB2BProductExcelParser();

  const bulkUpload = useBulkCreateB2BProducts();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    const { data, errors: parseErrors } = await processFile(file);

    if (data.length > 0 && parseErrors.length === 0) {
      setUploadStep('preview');
    } else if (parseErrors.length > 0) {
      setUploadStep('select');
    }
  };

  const handleUpload = async () => {
    if (previewData.length === 0) return;

    setUploadStep('uploading');
    setUploadProgress({ current: 0, total: previewData.length });

    try {
      await bulkUpload.mutateAsync(previewData);
      setUploadProgress({ current: previewData.length, total: previewData.length });
      setUploadStep('complete');
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStep('preview');
    }
  };

  const handleStartOver = () => {
    setUploadStep('select');
    setSelectedFile(null);
    setUploadProgress({ current: 0, total: 0 });
    clearData();
  };

  const getProgressValue = () => {
    if (uploadProgress.total === 0) return 0;
    return (uploadProgress.current / uploadProgress.total) * 100;
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload B2B Products
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <B2BProductUploadTemplate />

        {uploadStep === 'select' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Choose Excel File</p>
              <p className="text-muted-foreground mb-4">
                Select an Excel file containing your B2B product data
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isProcessing}
              />
              <label htmlFor="file-upload">
                <Button asChild disabled={isProcessing}>
                  <span className="cursor-pointer">
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Select File'
                    )}
                  </span>
                </Button>
              </label>
            </div>

            {selectedFile && (
              <Alert>
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  Selected file: {selectedFile.name}
                </AlertDescription>
              </Alert>
            )}

            {hasErrors && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    {errors.map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {uploadStep === 'preview' && hasData && (
          <div className="space-y-4">
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Successfully parsed {previewData.length} B2B products. Review the data below and click upload to proceed.
              </AlertDescription>
            </Alert>

            <B2BProductPreviewTable data={previewData} />

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleStartOver}>
                Start Over
              </Button>
              <div className="space-x-2">
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} disabled={bulkUpload.isPending}>
                  {bulkUpload.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    `Upload ${previewData.length} Products`
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {uploadStep === 'uploading' && (
          <div className="space-y-4">
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Uploading B2B products... {uploadProgress.current} of {uploadProgress.total}
              </AlertDescription>
            </Alert>
            <Progress value={getProgressValue()} className="w-full" />
          </div>
        )}

        {uploadStep === 'complete' && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Successfully uploaded {uploadProgress.total} B2B products!
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default B2BProductUpload;